import { Grid, Typography } from "@mui/material";
import { useMessage } from "hooks/intl";
import { Fragment } from "react";
import {
  AchievementActivity,
  AchievementCompleteRulePeriod,
  AchievementCompleteRulePeriodUnion,
  AchievementTriggers,
  AchievementTriggersUnion,
  BEAchievementLimit,
} from "types/achievements";
import { SelectedLimitType } from "types/campaigns";
import { OLDate } from "utilities/dateFormat";
import { formatNumberValue } from "utilities/numbers";
import { pickPluralizedUnit, pluralizeValueAdornment } from "utilities/strings";
import { useStyles } from "../AchievementForm.styles";

export const AchievementAvailability = (activity: AchievementActivity | {}) => {
  const { message } = useMessage();

  const getData = () => {
    if (!("operator" in activity)) {
      return;
    }

    switch (activity.operator) {
      case "is_after":
        return (
          <>
            {message({ id: "after" })} {OLDate(activity.data)}
          </>
        );
      case "is_before":
        return (
          <>
            {message({ id: "before" })} {OLDate(activity?.data)}
          </>
        );
      case "is_between":
        return (
          <>
            {message({ id: "between" })} {OLDate(activity?.data?.from)}
            {` ${message({ id: "and" }).toLocaleLowerCase()} `}
            {OLDate(activity?.data?.to)}
          </>
        );
      case "is_time_between":
        return (
          <>
            {message({ id: "between" })} {activity?.data?.from}
            {` ${message({ id: "and" }).toLocaleLowerCase()} `}
            {activity?.data?.to}
          </>
        );
      case "is_not_between":
        return (
          <>
            {message({ id: "notBetween" })} {OLDate(activity?.data?.from)}
            {` ${message({ id: "and" }).toLocaleLowerCase()} `}
            {OLDate(activity?.data?.to)}
          </>
        );
      case "is_day_of_month":
        return (
          <>
            {activity?.data} {message({ id: "dayOfMonth" }).toLowerCase()}
          </>
        );
      case "is_day_of_week":
      case "is_month_of_year":
        return (
          activity?.data
            ?.map((item: string) => message({ id: item }))
            .join(", ") || "-"
        );
      default:
        return message({ id: "alwaysAvailable" });
    }
  };

  return getData();
};

export const AchievementGoal = (selectedRule: {
  period: {
    consecutive?: number;
    type: AchievementCompleteRulePeriodUnion;
    value?: number;
  };
  periodGoal: any;
}) => {
  const { OVERALL, LAST_DAY } = AchievementCompleteRulePeriod;
  const { message } = useMessage();

  return selectedRule?.period?.type === OVERALL
    ? `${formatNumberValue(selectedRule?.periodGoal)} ${message({ id: "overall" }).toLocaleLowerCase()}`
    : message(
        { id: "achievementGoalString" },
        {
          periodGoal: formatNumberValue(selectedRule?.periodGoal),
          pluralizedTime: pickPluralizedUnit(
            selectedRule?.periodGoal,
            "time",
            "times",
          ),
          periods:
            selectedRule?.period?.type === LAST_DAY
              ? formatNumberValue(selectedRule?.period?.value)
              : formatNumberValue(selectedRule?.period?.consecutive),
          lastOperator:
            selectedRule?.period?.type === LAST_DAY
              ? message({ id: "last" }).toLowerCase()
              : "",
          consecutiveOperator:
            selectedRule?.period?.type !== LAST_DAY
              ? message({ id: "consecutive" }).toLowerCase()
              : "",
          pluralizedPeriodType: message({
            id: pluralizeValueAdornment(
              selectedRule?.period?.type,
              selectedRule?.period?.type === LAST_DAY
                ? selectedRule?.period?.value
                : selectedRule?.period?.consecutive,
            ),
          }).toLowerCase(),
        },
      );
};

export function DetailsList<
  T extends {
    left: string;
    right: string | React.ReactElement;
    rightClassName?: string;
    error?: boolean;
  },
>({ detailsList }: { detailsList: T[] }) {
  const classes = useStyles();

  return (
    <Grid container className={classes.summaryBox}>
      {detailsList.map((detail, index) => {
        const isLastItem = index === detailsList.length - 1;
        const itemMargin = isLastItem ? 0 : 16;
        const errorClassName = detail?.error ? classes.error : "";

        return (
          <Fragment key={index}>
            <Grid item xs={3} mb={itemMargin}>
              <Typography className={`${classes.summaryKey} ${errorClassName}`}>
                {detail.left}
              </Typography>
            </Grid>
            <Grid
              item
              xs={9}
              mb={itemMargin}
              className={`${detail.rightClassName || ""} ${classes.wrapText}  ${errorClassName}`}
            >
              {detail.right}
            </Grid>
          </Fragment>
        );
      })}
    </Grid>
  );
}

export const prepareAchievementLimitToBEFormat = (
  limit: SelectedLimitType,
): BEAchievementLimit => {
  if (!(limit && "interval" in limit)) {
    return;
  }
  switch (limit?.interval?.type) {
    case "noLimit":
      return {};
    case "inTotal":
      return { value: limit?.value };
    default:
      return { ...limit, interval: { ...limit?.interval, value: 1 } };
  }
};

export const getTriggerLabel = (trigger: AchievementTriggersUnion) =>
  trigger === AchievementTriggers.TRANSACTION ? "transaction" : "customEvent";
